import React, { useState, useEffect } from 'react';
import VaultSidebar from '../../components/vault/VaultSidebar';
import VaultHeader from '../../components/vault/VaultHeader';
import SharedPasswordsList from '../../components/vault/SharedPasswordsList';
import SharedByMeList from '../../components/vault/SharedByMeList';
import {
    PlusIcon,
    ShareIcon,
    UserGroupIcon,
    ArrowRightIcon,
    ChartBarIcon,
    ShieldCheckIcon,
    ClockIcon
} from '@heroicons/react/24/outline';
import ShareItemsModal from '../../components/vault/ShareItemsModal';
import { ShareService } from '../../services/share.service';
import { useWorkspace } from '../../contexts/WorkspaceContext';

const SharingCenterPage: React.FC = () => {
    const [activeTab, setActiveTab] = useState('shared-by-me');
    const [shareItemsModalOpen, setShareItemsModalOpen] = useState(false);
    const [refreshTrigger, setRefreshTrigger] = useState(0);
    const [stats, setStats] = useState({
        sharedByMe: 0,
        sharedWithMe: 0,
        totalShares: 0,
        expiringShares: 0
    });
    const { activeWorkspace } = useWorkspace();

    useEffect(() => {
        loadStats();
    }, [activeWorkspace]);

    const loadStats = async () => {
        try {
            const [sharedByMeItems, sharedWithMeItems] = await Promise.all([
                ShareService.getItemsSharedByMe(activeWorkspace?.id || ''),
                ShareService.getSharedItems(activeWorkspace?.id || '')
            ]);

            // Calculate expiring shares (within 7 days)
            const now = new Date();
            const expiringShares = sharedByMeItems.filter(item => {
                if (!item.expires_at) return false;
                const expiryDate = new Date(item.expires_at);
                const diffDays = Math.ceil((expiryDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
                return diffDays <= 7 && diffDays > 0;
            }).length;

            setStats({
                sharedByMe: sharedByMeItems.length,
                sharedWithMe: sharedWithMeItems.length,
                totalShares: sharedByMeItems.length + sharedWithMeItems.length,
                expiringShares
            });
        } catch (error) {
            console.error('Failed to load sharing stats:', error);
        }
    };

    const handleShareSuccess = () => {
        setShareItemsModalOpen(false);
        setRefreshTrigger(prev => prev + 1);
        loadStats(); // Refresh stats after sharing
    };

    return (
        <div className="flex h-screen bg-gray-100 dark:bg-gray-900">
            <VaultSidebar />

            <div className="flex flex-col flex-1 overflow-hidden">
                <VaultHeader />

                <main className="flex-1 overflow-y-auto bg-gray-50 dark:bg-gray-900">
                    {/* Hero Banner Section */}
                    <div className="relative bg-gradient-to-br from-primary-600 via-primary-700 to-primary-800 dark:from-primary-800 dark:via-primary-900 dark:to-gray-900 overflow-hidden">
                        {/* Background Pattern */}
                        <div className="absolute inset-0 opacity-10">
                            <div className="absolute top-0 right-0 w-96 h-48 bg-white rounded-full -translate-y-24 translate-x-48"></div>
                            <div className="absolute bottom-0 left-0 w-72 h-36 bg-white rounded-full translate-y-18 -translate-x-36"></div>
                            <div className="absolute top-1/2 left-1/4 w-32 h-32 bg-white rounded-full"></div>
                            <div className="absolute top-1/3 right-1/3 w-24 h-24 bg-white rounded-full"></div>
                        </div>

                        <div className="relative px-6 py-12">
                            <div className="max-w-7xl mx-auto">
                                {/* Header Content */}
                                <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between mb-8">
                                    <div className="mb-6 lg:mb-0">
                                        <div className="flex items-start space-x-4 mb-4">
                                            <div className="p-3 bg-white/20 backdrop-blur-sm rounded-xl border border-white/30">
                                                <ShareIcon className="h-8 w-8 text-white" />
                                            </div>
                                            <div>
                                                <h1 className="text-4xl lg:text-5xl font-bold text-white mb-2 leading-tight">
                                                    Sharing Center
                                                </h1>
                                                <div className="flex items-center space-x-2 text-white/80">
                                                    <UserGroupIcon className="h-5 w-5" />
                                                    <span className="text-sm">Secure Collaboration Hub</span>
                                                </div>
                                            </div>
                                        </div>
                                        <p className="text-xl text-white/90 max-w-2xl leading-relaxed font-sans">
                                            Securely share passwords, notes, and sensitive data with team members.
                                            Manage permissions, track access, and maintain control over your shared items.
                                        </p>
                                    </div>
                                </div>

                                {/* Stats Cards */}
                                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-8 max-w-5xl mx-auto">
                                    <div className="bg-white/20 backdrop-blur-sm rounded-xl p-4 border border-white/30 hover:bg-white/25 transition-all duration-200">
                                        <div className="flex items-center space-x-3">
                                            <div className="p-2 bg-white/20 rounded-lg">
                                                <ShareIcon className="w-6 h-6 text-white" />
                                            </div>
                                            <div>
                                                <div className="text-2xl font-bold text-white">
                                                    {stats.sharedByMe}
                                                </div>
                                                <div className="text-xs text-white/80 uppercase tracking-wide">
                                                    Shared by Me
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="bg-white/20 backdrop-blur-sm rounded-xl p-4 border border-white/30 hover:bg-white/25 transition-all duration-200">
                                        <div className="flex items-center space-x-3">
                                            <div className="p-2 bg-white/20 rounded-lg">
                                                <UserGroupIcon className="w-6 h-6 text-white" />
                                            </div>
                                            <div>
                                                <div className="text-2xl font-bold text-white">
                                                    {stats.sharedWithMe}
                                                </div>
                                                <div className="text-xs text-white/80 uppercase tracking-wide">
                                                    Shared with Me
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="bg-white/20 backdrop-blur-sm rounded-xl p-4 border border-white/30 hover:bg-white/25 transition-all duration-200">
                                        <div className="flex items-center space-x-3">
                                            <div className="p-2 bg-white/20 rounded-lg">
                                                <ChartBarIcon className="w-6 h-6 text-white" />
                                            </div>
                                            <div>
                                                <div className="text-2xl font-bold text-white">
                                                    {stats.totalShares}
                                                </div>
                                                <div className="text-xs text-white/80 uppercase tracking-wide">
                                                    Total Shares
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Enhanced Tab Navigation */}
                                <div className="flex justify-center">
                                    <div className="bg-white/10 backdrop-blur-md rounded-2xl p-2 border border-white/20">
                                        <nav className="flex space-x-2" aria-label="Tabs">
                                            <button
                                                onClick={() => setActiveTab('shared-by-me')}
                                                className={`relative px-6 py-3 rounded-xl font-medium text-sm transition-all duration-300 whitespace-nowrap ${activeTab === 'shared-by-me'
                                                    ? 'bg-white text-blue-600 shadow-lg scale-105'
                                                    : 'text-white/80 hover:text-white hover:bg-white/20'
                                                    }`}
                                            >
                                                <div className="flex items-center space-x-2">
                                                    <ShareIcon className="h-5 w-5" />
                                                    <span>Shared by Me</span>
                                                </div>
                                                {activeTab === 'shared-by-me' && (
                                                    <div className="absolute -top-1 -right-1 w-3 h-3 bg-green-400 rounded-full border-2 border-white"></div>
                                                )}
                                            </button>

                                            <button
                                                onClick={() => setActiveTab('shared-with-me')}
                                                className={`relative px-6 py-3 rounded-xl font-medium text-sm transition-all duration-300 whitespace-nowrap ${activeTab === 'shared-with-me'
                                                    ? 'bg-white text-blue-600 shadow-lg scale-105'
                                                    : 'text-white/80 hover:text-white hover:bg-white/20'
                                                    }`}
                                            >
                                                <div className="flex items-center space-x-2">
                                                    <UserGroupIcon className="h-5 w-5" />
                                                    <span>Shared with Me</span>
                                                </div>
                                                {activeTab === 'shared-with-me' && (
                                                    <div className="absolute -top-1 -right-1 w-3 h-3 bg-green-400 rounded-full border-2 border-white"></div>
                                                )}
                                            </button>
                                        </nav>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Content Section */}
                    <div className="container mx-auto px-6 py-8">
                        {/* Tab Content */}
                        <div className="space-y-6">
                            <div className="bg-white dark:bg-gray-800 shadow-xl rounded-2xl overflow-hidden border border-gray-200 dark:border-gray-700">
                                <div className="p-8">
                                    {activeTab === 'shared-with-me' ? (
                                        <SharedPasswordsList />
                                    ) : (
                                        <SharedByMeList key={refreshTrigger} />
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </main>

                {/* Floating Action Button */}
                <button
                    onClick={() => setShareItemsModalOpen(true)}
                    className="fixed bottom-4 right-4 sm:bottom-6 sm:right-6 bg-primary-600 hover:bg-primary-700 text-white rounded-full p-3 sm:p-4 shadow-lg focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 flex items-center justify-center z-10"
                    aria-label="Share Items"
                >
                    <ShareIcon className="h-5 w-5 sm:h-6 sm:w-6" />
                    <span className="ml-2 hidden sm:inline">Share Items</span>

                </button>
            </div>

            <ShareItemsModal
                isOpen={shareItemsModalOpen}
                onClose={() => setShareItemsModalOpen(false)}
                onSuccess={handleShareSuccess}
            />
        </div>
    );
};

export default SharingCenterPage;